import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";

import { useCallback, useEffect, useMemo, useState } from "react";

import { green, grey } from "@mui/material/colors";
import { GlobalState } from "context/GlobalContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { publishTableColumn, selectedShootCostingPagepath } from "components/PimDashboard/data";
import { costingColumns, shootCostingUrlKey, CreatedAtColumn, totalCostColumn } from "../CreateShootCosting/data";
import { converAttrToDatagridCol, createdByColumns, publishColumns } from "data/globalData";
import { FileDownloadRounded, FilterAltRounded, PlaylistAddRounded } from "@mui/icons-material";
import FileSaver from "file-saver";
import FilterModal from "./FilterModal/FilterModal";
import AddColumnModal from "./AddColumnModal/AddColumnModal";
import { allowAccess } from "utils/userAccessFun";
import { custom, departments } from "data/authData";
import { AuthState } from "context/AuthContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { lsFilterkeyStr } from "./data";
import moment from "moment";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";

const pageFilterLgKey = "studio_costing_table_attr";

const defaultCostingAttrColumns = [...Object.values(costingColumns), totalCostColumn, CreatedAtColumn];
const costingTableColumns = [...defaultCostingAttrColumns, ...createdByColumns, ...Object.values(publishColumns)];
const costingTableColumnsObj = costingTableColumns.reduce((acc, row) => {
	if (row?.key) acc[row.key] = row;
	return acc;
}, {});

const hasFilterValue = (value) =>
	value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && value.length === 0);

const readStoredFilter = () => {
	try {
		const raw = localStorage.getItem(lsFilterkeyStr);
		const parsed = raw ? JSON.parse(raw) : {};
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
		return Object.fromEntries(Object.entries(parsed).filter(([, value]) => hasFilterValue(value)));
	} catch {
		return {};
	}
};

const toTanstackColumn = (col) => {
	if (!col) return null;
	const key = col.key || col.field;
	if (!key) return null;
	const muiValueGetter = col.valueGetter;
	return {
		...col,
		key,
		name: col.name || col.headerName || key,
		align: col.align || "center",
		headerAlign: col.headerAlign || "center",
		minWidth: col.minWidth || 110,
		valueGetter:
			typeof muiValueGetter === "function"
				? ({ value, row }) => {
						try {
							return muiValueGetter(value, row);
						} catch {
							return value ?? "";
						}
					}
				: undefined,
	};
};

const ShootCostingDashboard = () => {
	const { user } = AuthState();
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const [tableData, setTableData] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [tableStatusCount, setTableStatusCount] = useState({
		total: 0,
		pending: 0,
		published: 0,
	});
	const [filterModal, setFilterModal] = useState(false);
	const [addColumnModal, setAddColumnModal] = useState(false);

	const [attrColumns, setAttrColumns] = useState(() => {
		if (localStorage.getItem(pageFilterLgKey)) {
			return JSON.parse(localStorage.getItem(pageFilterLgKey))
				?.map((row) => ({ ...costingTableColumnsObj?.[row] }))
				.map((row) => converAttrToDatagridCol(row));
		}
		return defaultCostingAttrColumns.map((row) => converAttrToDatagridCol(row));
	});

	const [filterColumnVal, setFilterColumnVal] = useState(readStoredFilter);
	const [filterColumns, setFilterColumns] = useState([]);

	useEffect(() => {
		setFilterColumns(costingTableColumns.filter((row) => hasFilterValue(filterColumnVal?.[row.key])));
	}, [filterColumnVal]);

	const attrColumnKey = (attrColumns || [])
		.map((row) => row?.field || row?.key)
		.filter(Boolean)
		.join("|");

	const fetchData = useCallback(
		async (limit, offset, filter) => {
			try {
				const { data } = await axios.post(`/api/studio/shoot_costings/${offset}`, {
					filter: filter ?? {},
					limit,
				});
				const count = data?.result?.count?.[0] ?? {};
				const rows = data?.result?.rows ?? [];
				setTableStatusCount({
					total: count.total ?? 0,
					pending: count.pending ?? 0,
					published: count.published ?? 0,
				});
				return {
					rows,
					total_rows_count: Number(count.total) || rows.length,
				};
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: parseErrorMessage(error),
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				throw error;
			}
		},
		[setSnackBar, setSnackBarOpen, snackBar],
	);

	const navigate = useNavigate();

	const handleRowSelect = (data) => {
		navigate(`/${selectedShootCostingPagepath}?${shootCostingUrlKey}=${data.shoot_costing_id}`);
	};

	const handleDownloadClick = async () => {
		setLoadingOpen(true);
		try {
			await axios({
				url: `/api/sourcing/download_all_costings`,
				method: "POST",
				responseType: "blob",
				data: { filter: filterColumnVal },
			}).then((res) => {
				const url = URL.createObjectURL(res.data);
				FileSaver.saveAs(url, `Shoot_Costings_${new Date().toISOString()}.xlsx`);
				setLoadingOpen(false);
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			setLoadingOpen(false);
		}
	};

	const handlePublishClick = useCallback(
		async (e, styleData) => {
			e.stopPropagation();
			setLoadingOpen(true);
			try {
				const res = await axios.post(`/api/sourcing/publish_costing`, {
					shoot_costing_id: styleData.shoot_costing_id,
					published_date: moment().format("YYYY-MM-DD"),
				});
				setTableData((prev) =>
					prev.map((tableRow) => {
						if (tableRow.shoot_costing_id === styleData.shoot_costing_id) {
							return { ...tableRow, ...res.data.result };
						}
						return tableRow;
					}),
				);
				setTableStatusCount((prev) => ({ ...prev, published: +prev.published + 1, pending: prev.pending - 1 }));
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "Costing Published Successfully",
					severity: "success",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				setLoadingOpen(false);
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: parseErrorMessage(error),
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				setLoadingOpen(false);
			}
		},
		[setLoadingOpen, setSnackBar, setSnackBarOpen, snackBar],
	);

	const tableColumns = useMemo(() => {
		const publishColumn = toTanstackColumn(
			publishTableColumn(handlePublishClick, !allowAccess(custom.SHOOT_COSTING_PUBLISHER, user, departments.SOURCING.key)),
		);

		return [
			...(attrColumns || []).map((col) => toTanstackColumn(col)),
			...createdByColumns.map((col) => ({
				...col,
				align: "center",
				headerAlign: "center",
			})),
			publishColumn,
		].filter(Boolean);
	}, [attrColumns, handlePublishClick, user]);

	return (
		<Box sx={{ height: "calc(100vh - 135px)", p: 1, boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 1 }}>
			<Box display="flex" columnGap={1} pt={0.5} flexWrap="wrap" alignItems="center" rowGap="5px">
				<Button
					variant="contained"
					size="small"
					startIcon={<FilterAltRounded />}
					onClick={() => setFilterModal(true)}
					sx={{ textTransform: "capitalize" }}>
					Filter
				</Button>
				:
				{!filterColumns?.length && (
					<Typography variant="body1" fontSize={16} color={grey[700]}>
						None
					</Typography>
				)}
				{filterColumns?.map((row) => (
					<Chip
						key={row.key}
						label={`${row.name} : ${
							Array.isArray(filterColumnVal[row.key]) ? filterColumnVal[row.key]?.join(", ") : filterColumnVal?.[row.key]
						}`}
						size="small"
					/>
				))}
			</Box>

			<Box sx={{ flex: 1, minHeight: 0 }}>
				<TanstackDatagrid
					key={`costings-grid-${attrColumnKey}`}
					columns={tableColumns}
					pageData={tableData}
					setPageData={setTableData}
					rowCount={rowCount}
					setRowCount={setRowCount}
					rowHeight={50}
					filterObj={filterColumnVal}
					setFilterObj={setFilterColumnVal}
					disableColumnFilter
					disableColumnSort
					isLoading={false}
					disablePagination
					emptyMessage="No matching records"
					handleRowClick={({ row }) => handleRowSelect(row)}
					getServerSideData={fetchData}
					getRowId={(row) => row?.shoot_costing_id}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							py: 0.5,
							width: "100%",
							minHeight: 44,
						}}>
						<Box display="flex" alignItems="center" columnGap={1} flexShrink={0}>
							<Typography variant="body1">
								Total: <span style={{ color: grey[700] }}>{tableStatusCount.total}</span>
							</Typography>
							<Divider orientation="vertical" flexItem />
							<Typography variant="body1">
								Published: <span style={{ color: green[600] }}>{tableStatusCount.published ?? 0}</span>
							</Typography>
							<Divider orientation="vertical" flexItem />
							<Typography variant="body1" fontWeight={600}>
								Pending: <span style={{ color: "red" }}>{tableStatusCount.pending ?? 0}</span>
							</Typography>
						</Box>
						<Box sx={{ flex: 1 }} />
						<Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
							<Button
								variant="contained"
								sx={{ textTransform: "capitalize" }}
								size="small"
								onClick={() => setAddColumnModal(true)}
								startIcon={<PlaylistAddRounded />}>
								Manage Columns
							</Button>
							<Button
								variant="contained"
								size="small"
								sx={{ textTransform: "capitalize" }}
								onClick={handleDownloadClick}
								startIcon={<FileDownloadRounded />}>
								Download
							</Button>
						</Stack>
					</Box>
				</TanstackDatagrid>
			</Box>

			{filterModal && (
				<FilterModal
					modal={filterModal}
					handleCloseModal={() => setFilterModal(false)}
					filterColumns={filterColumns}
					setFilterColumns={setFilterColumns}
					filterColumnVal={filterColumnVal}
					setFilterColumnVal={setFilterColumnVal}
					localStorageStr={lsFilterkeyStr}
					allColumns={costingTableColumns}
				/>
			)}
			<AddColumnModal
				modal={addColumnModal}
				handleCloseModal={() => setAddColumnModal(false)}
				attrColumns={attrColumns}
				setAttrColumns={setAttrColumns}
				localStorageStr={pageFilterLgKey}
				allColumns={costingTableColumns}
				allColumnsObj={costingTableColumnsObj}
				defaultColumns={defaultCostingAttrColumns}
			/>
		</Box>
	);
};

export default ShootCostingDashboard;
